/** @format */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AcademicCapIcon, PlayIcon, BuildingOfficeIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useCourseProgress } from '../hooks/useCourseProgress';
import { Classroom } from '../types';

interface CourseCardProps {
  course: any;
  showProgress?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  showEditButton?: boolean;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, showProgress = true, onClick, onEdit, showEditButton = false }) => {
  const { getOverallProgress, loading: progressLoading } = useCourseProgress(course.id);
  const progress = showProgress ? getOverallProgress() : 0;
  const [showFullImage, setShowFullImage] = useState(false);

  return (
    <>
      <div className={`group transform hover:scale-[1.02] transition-all duration-300 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden relative">
          {/* Course Image - Dynamic height based on content */}
          <div className="relative w-full bg-gray-100 overflow-hidden">
            {course.image_url ? (
              <img 
                src={course.image_url} 
                alt={course.name}
                className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500 bg-gray-50"
                loading="lazy"
                decoding="async"
                sizes="100vw"
                onLoad={(e) => {
                  // Remove loading state when image loads
                  e.currentTarget.style.opacity = '1';
                }}
                style={{ opacity: 0, transition: 'opacity 0.3s ease-in-out' }}
              />
            ) : (
              <div className="w-full h-60 flex items-center justify-center">
                <AcademicCapIcon className="h-16 w-16 text-gray-400" />
              </div>
            )}

            {/* Expand Image Icon */}
            {course.image_url && (
              <button
                onClick={() => setShowFullImage(true)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 hover:text-blue-600 p-1.5 rounded-full shadow-sm transition-all duration-200 opacity-0 group-hover:opacity-100"
                title="View full image"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            )}

            {/* Progress Badge */}
            {showProgress && (
              <div className="absolute top-2 left-2">
                <div className="bg-white px-2 py-1 rounded text-gray-800 flex items-center shadow-sm">
                  <span className="text-xs font-bold text-blue-600">{progressLoading ? '...' : `${progress}%`}</span>
                </div>
              </div>
            )}

            {/* Status Badge */}
            {course.status === 'active' && (
              <div className="absolute top-2 right-2">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-green-500 text-white shadow-sm">
                  Active
                </span>
              </div>
            )}

            {/* Expiry Badge */}
            {course.expire_date && (
              <div className="absolute bottom-2 left-2">
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold shadow-sm ${
                  (() => {
                    const now = new Date();
                    const expiry = new Date(course.expire_date);
                    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (daysUntilExpiry < 0) return 'bg-red-500 text-white'; // Expired
                    if (daysUntilExpiry <= 7) return 'bg-orange-500 text-white'; // Expiring soon
                    if (daysUntilExpiry <= 30) return 'bg-yellow-500 text-white'; // Expiring this month
                    return 'bg-green-500 text-white'; // Plenty of time
                  })()
                }`}>
                  {(() => {
                    const now = new Date();
                    const expiry = new Date(course.expire_date);
                    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (daysUntilExpiry < 0) {
                      return 'Expired';
                    } else if (daysUntilExpiry === 0) {
                      return 'Today';
                    } else if (daysUntilExpiry === 1) {
                      return 'Tomorrow';
                    } else if (daysUntilExpiry <= 7) {
                      return `${daysUntilExpiry}d`;
                    } else if (daysUntilExpiry <= 30) {
                      return `${Math.ceil(daysUntilExpiry / 7)}w`;
                    } else {
                      return 'Active';
                    }
                  })()}
                </span>
              </div>
            )}

            {/* Price Badge */}
            <div className="absolute bottom-2 right-2">
              <span className={`px-2 py-1 text-xs font-bold rounded shadow-sm ${
                course.price_status === "Free" || course.pricee === "0"
                  ? "bg-green-600 text-white"
                  : "bg-slate-800 text-white"
              }`}>
                {course.price_status === "Free" || course.pricee === "0"
                  ? "FREE"
                  : `₵${course.pricee || '0'}`}
              </span>
            </div>
          </div>
        
          {/* Course Content */}
          <div className="p-3 flex flex-col">
            {/* Title */}
            <h4 className="font-bold text-gray-900 text-sm leading-tight mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors">
              {course.name}
            </h4>
            
            {/* Rating Display */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center">
                <span className="text-sm font-bold text-gray-900 mr-1">
                  {course.rating > 0 ? course.rating.toFixed(1) : 'No ratings'}
                </span>
                <div className="flex items-center">
                  {[...Array(5)].map((_, index) => (
                    <svg
                      key={index}
                      className={`h-3 w-3 ${
                        course.rating > 0 && index < Math.floor(course.rating)
                          ? 'text-yellow-400'
                          : course.rating > 0 && index < course.rating
                          ? 'text-yellow-400'
                          : 'text-yellow-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <span className="text-xs text-gray-500">
                {course.rating > 0 ? `(${(course as any).reviews_count || 0} reviews)` : '(Be the first to rate)'}
              </span>
            </div>
            
            {/* Tags Row */}
            <div className="flex flex-wrap items-center gap-1 mb-3">
              {/* Program Tag */}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="truncate max-w-[80px]">
                  {Array.isArray(course.program) ? course.program[0] : course.program || 'General'}
                </span>
              </span>
              
              {/* Category Tag */}
              {course.cat_id && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM7 7a1 1 0 000 2h6a1 1 0 100-2H7zM7 11a1 1 0 000 2h6a1 1 0 100-2H7z" />
                  </svg>
                  <span className="truncate max-w-[80px]">
                    {course.cat_id}
                  </span>
                </span>
              )}
              
              {/* Lessons Count */}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">
                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="truncate max-w-[80px]">
                  {course.lessons_count || 0} Lesson{course.lessons_count !== 1 ? 's' : ''}
                </span>
              </span>
            </div>
            
            {/* Progress and Time Info */}
            {showProgress && (
              <div className="space-y-2 mb-3 p-2 bg-blue-50 rounded border border-blue-100">
                {/* Progress Bar */}
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span className="flex items-center">
                      <svg className="h-3 w-3 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Learning Progress
                    </span>
                    <span className="text-blue-600 font-bold">{progressLoading ? '...' : `${progress}%`}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-1.5 rounded-full transition-all duration-300" 
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Time Spent */}
                <div className="flex items-center text-xs text-gray-600">
                  <svg className="h-3 w-3 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Time: 0h 0m
                </div>
              </div>
            )}
            
            {/* Course Resources */}
            <div className="mb-3 space-y-2">
              {/* Sections and Lessons */}
              {Array.isArray(course.sections) && course.sections.length > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-600 p-1.5 bg-green-50 rounded border border-green-100">
                  <span className="flex items-center min-w-0 flex-1">
                    <svg className="h-3 w-3 mr-1.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    <span className="font-medium">{course.sections.length} sections</span>
                  </span>
                  <span className="flex items-center ml-2 flex-shrink-0">
                    <svg className="h-3 w-3 mr-1.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                    </svg>
                    <span className="font-medium">{course.lessons_count || 0} lessons</span>
                  </span>
                </div>
              )}
              
              {/* Classroom Access */}
              {Array.isArray(course.classrooms) && course.classrooms.length > 0 && (
                <div className="text-xs text-gray-600 space-y-1.5 p-1.5 bg-orange-50 rounded border border-orange-100">
                  <div className="flex items-center">
                    <svg className="h-3 w-3 mr-1.5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    <span className="font-medium text-gray-700">
                      {course.classrooms.length} classroom{course.classrooms.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {course.classrooms.slice(0, 3).map((c: Classroom) => (
                      <span key={c.id} className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200 text-xs">
                        <svg className="h-2.5 w-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        {c.name}
                      </span>
                    ))}
                    {course.classrooms.length > 3 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200 text-xs">
                        <svg className="h-2.5 w-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" />
                        </svg>
                        +{course.classrooms.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="w-full relative z-10 mt-4 space-y-2">
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/app/course-learning/${course.id}`}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold rounded transition-all duration-200"
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                {progress > 0 ? 'Continue Learning' : 'Start Learning'}
              </Link>
              
              {/* Edit Button - Only show for authors/admins */}
              {showEditButton && onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded transition-all duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit Course
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      {showFullImage && course.image_url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
            <img
              src={course.image_url}
              alt={course.name}
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg transition-all duration-200"
              title="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseCard;
